import React, { useState, useEffect } from "react";
import { Card } from "../../../../Components/ui/card";
import { Label } from "../../../../Components/ui/label";
import { Input } from "../../../../Components/ui/input";
import { Button } from "../../../../Components/ui/button";
import { MultiSelectFromCountry } from "../../../../Components/ui/multi-select-from-country";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../../Components/ui/accordion";
import { Dialog, DialogContent } from "../../../../Components/ui/dialog";
import CreateContentWhatsapp from "../Components/CreateContentWhatsappCard";
import CreateContentSms from "../Components/CreateContentSMSCard";
import { useCampaignContext } from "../CampaignContext";
import { useCampaignValidators } from "../hooks/useCampaignValidators";
import { RootState } from '@/src/State/store';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { setSelectedFromCountryISOs } from "../../../../State/slices/AdvertiserAccountSlice";
import { useContext } from "react-resizable-panels/dist/declarations/src/vendor/react";

interface CountryTraveller {
  country: string;
  count: string;
}

interface ChannelAccordionSectionProps {
  channel: string;
  data: CountryTraveller[];
  onChange: (index: number, field: keyof CountryTraveller, value: string) => void;
  total: number;
  onOpenCreate: () => void;
  contentExists?: boolean;
  isUpdateMode: boolean;
}

const formatChannel = (channel: string) => {
  if (channel.toLowerCase() === "sms") return "SMS";
  if (channel.toLowerCase() === "whatsapp") return "WhatsApp";
  return channel.charAt(0).toUpperCase() + channel.slice(1).toLowerCase();
};

const ChannelAccordionSection: React.FC<ChannelAccordionSectionProps> = ({
  channel,
  data,
  onChange,
  total,
  onOpenCreate,
  contentExists,
  isUpdateMode,
}) => {
  const {
    validateTravellerCount,
    getCountError
  } = useCampaignValidators();
  const isWhatsapp = channel.toLowerCase() === "whatsapp";
  const [isTestMode, setIsTestMode] = useState(false);
const [testCount, setTestCount] = useState("");


  const { isViewMode } = useCampaignContext();

  const handleBlur = (index: number, value: string) => {
    if (isUpdateMode) return; // Skip validation in update mode

    const channelKey = channel.toLowerCase() as 'whatsapp' | 'sms';
    validateTravellerCount(value, channelKey, index);
  };

  const getFieldError = (index: number): string | undefined => {
    const channelKey = channel.toLowerCase() as 'whatsapp' | 'sms';
    return getCountError(channelKey, index);
  };

  return (
    <AccordionItem value={channel}>
      <AccordionTrigger className="text-sm font-medium text-[#020617]">
        {formatChannel(channel)}
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-4">
          {data.map((item, index) => (
            <div key={index} className="space-y-1">
              <div className="flex gap-4">
                <Input
                  placeholder=""
                  value={item.country}
                  disabled
                  className="w-48"
                />
                <div className="flex-1">
                  <Input
                    placeholder="Number of travellers you want to reach"
                    value={item.count || ""}
                    onChange={(e) => onChange(index, "count", e.target.value)}
                    onBlur={(e) => handleBlur(index, e.target.value)}
                    className="w-full"
                    disabled={isUpdateMode}
                  />
                  {getFieldError(index) && (
                    <p className="text-red-500 text-xs mt-1 font-sans italic ml-1 text-left">
                      {getFieldError(index)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>



        <div className="mt-4">
          <Label
            htmlFor={`total-${channel}`}
            className="block text-sm font-medium text-[#020617] mb-1"
          >
            Total Traveller Count - {formatChannel(channel)}
          </Label>
          <Input
            id={`total-${channel}`}
            type="text"
            value={total.toString()}
            disabled
            className="w-full text-[#334155] text-sm font-normal bg-[#E2E8F0] cursor-not-allowed"
          />
        </div>

        <div className="mt-1 w-full flex justify-center">
          <Button
            className="bg-[#3A85F7] text-white px-4 py-2 rounded-md w-[135px] h-[40px] text-sm"
            onClick={onOpenCreate}
          >
            {isViewMode ? "View Content" : contentExists ? "Update Content" : "Create Content"}
          </Button>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

const CampaignChannelCard: React.FC = () => {
  const {
    whatsappContent,
    setWhatsappContent,
    smsContent,
    setSmsContent,
    whatsappData,
    setWhatsappData,
    smsData,
    setSmsData,
    partsCount,
    setEstimatedMessageNeeded,
    whatsappCountries,
    smsCountries,
    channelOptions,
    contentError,
    updateCampaignId,
    updateModeTravellerCount,
    setUpdateModeChannelOptions,
    updateModeChannelOptions,
    targetCountries,
    setSelectedChannels,
    selectedChannels,
    isUpdateMode,
    isSubmitAttempted,
    setMinLimit,
    setMaxLimit,
    draftModeTravellerCount,
    isDraftMode,
    isViewMode,
    draftModeChannelOptions,
    setDraftModeChannelOptions
  } = useCampaignContext();


  const selectedFromCountryISOs = useSelector((state: RootState) => state.advertiserAccount.selectedFromCountryISOs);

  // const { GetTravellerCount } = useCampaignValidators();

  const [isWhatsappDialogOpen, setIsWhatsappDialogOpen] = useState(false);
  const [isSmsDialogOpen, setIsSmsDialogOpen] = useState(false);
  const [previousSelectedChannels, setPreviousSelectedChannels] = useState<string[]>([]);

  const apiUrlAdminAcc = useSelector(
    (state: RootState) => state.authentication.adminUrl
  );
  const workspaceId = useSelector((state: RootState) => state.authentication.workspace_id);

  // Initialize traveller count limits when component mounts
  // useEffect(() => {
  //   GetTravellerCount();
  // }, []);

  // Track previous selected channels
  useEffect(() => {
    setPreviousSelectedChannels(selectedChannels);
  }, [selectedChannels]);

  // Cleanup function to clear channel data when channel is removed
  const cleanupChannelData = (removedChannel: string) => {
    if (removedChannel === "whatsapp") {
      // Clear WhatsApp data and content
      setWhatsappData(prev => prev.map(item => ({ ...item, count: "" })));
      setWhatsappContent("");
    } else if (removedChannel === "sms") {
      // Clear SMS data and content
      setSmsData(prev => prev.map(item => ({ ...item, count: "" })));
      setSmsContent("");
    }
  };

  // Custom onValueChange handler to detect removed channels
  const handleChannelChange = (newSelectedChannels: string[]) => {
    // Find removed channels by comparing with previous selection
    const removedChannels = previousSelectedChannels.filter(
      channel => !newSelectedChannels.includes(channel)
    );

    // Clean up data for each removed channel
    removedChannels.forEach(removedChannel => {
      cleanupChannelData(removedChannel);
    });

    // Update selected channels
    setSelectedChannels(newSelectedChannels);
  };


  // API for Traveller Count Validation
  const GetTravellerCountByWorkspaceId = async () => {
    console.log("Called GetTravellerCountByWorkspaceId");
    try {
      const res = await axios.get(`${apiUrlAdminAcc}/GetTravellerCountByWorkspaceId`, {
        params: { workspaceId },
      });

      if (res.data.status === "Success" && res.data.travellerCount) {
        const { min, max } = res.data.travellerCount;
        setMinLimit(min?.toString() || "");
        setMaxLimit(max?.toString() || "");
      } else {
        setMinLimit("");
        setMaxLimit("");
      }
    } catch (err) {
      setMinLimit("");
      setMaxLimit("");
    }
  };


  useEffect(() => {
    GetTravellerCountByWorkspaceId();
  }, [])

  useEffect(() => {
    setWhatsappData((prev) =>
      whatsappCountries.map(({ name }) =>
        prev.find((row) => row.country === name) || { country: name, count: "" }
      )
    );
  }, [whatsappCountries]);

  useEffect(() => {
    setWhatsappData((prev) =>
      prev.filter((row) => whatsappCountries.some((c) => c.name === row.country))
    );
  }, [whatsappCountries]);

  useEffect(() => {
    setSmsData((prev) =>
      smsCountries.map(({ name }) =>
        prev.find((row) => row.country === name) || { country: name, count: "" }
      )
    );
  }, [smsCountries]);

  useEffect(() => {
    setSmsData((prev) =>
      prev.filter((row) => smsCountries.some((c) => c.name === row.country))
    );
  }, [smsCountries]);

  const calculateTotal = (data: CountryTraveller[]) =>
    data.reduce((total, item) => total + (parseInt(item.count) || 0), 0);

  const calculateEstimatedMessageNeeded = (
    whatsappData: CountryTraveller[],
    smsData: CountryTraveller[],
    partsCount: number
  ): number => {
    const totalWhatsapp = whatsappData.reduce((sum, item) => {
      const num = parseInt(item.count);
      return sum + (isNaN(num) ? 0 : num);
    }, 0);

    const totalSMS = smsData.reduce((sum, item) => {
      const num = parseInt(item.count);
      return sum + (isNaN(num) ? 0 : num * partsCount);
    }, 0);

    return totalWhatsapp + totalSMS;
  };

  useEffect(() => {
    const estimate = calculateEstimatedMessageNeeded(whatsappData, smsData, partsCount);
    setEstimatedMessageNeeded(estimate);
  }, [whatsappData, smsData, partsCount]);

  useEffect(() => {
    const allData = [...whatsappData, ...smsData];

    const countriesWithCounts = allData
      .filter((row) => row.count.trim() !== "")
      .map((row) => {
        const found =
          whatsappCountries.find((c) => c.name === row.country) ||
          smsCountries.find((c) => c.name === row.country);
        return found?.iso;
      })
      .filter(Boolean) as string[];

    setSelectedFromCountryISOs(Array.from(new Set(countriesWithCounts)));
  }, [whatsappData, smsData]);

  const updateWhatsappData = (index: number, field: keyof CountryTraveller, value: string) => {
    setWhatsappData((data) =>
      data.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const updateSmsData = (index: number, field: keyof CountryTraveller, value: string) => {
    setSmsData((data) =>
      data.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  useEffect(() => {
    if (!updateCampaignId || !updateModeTravellerCount) return;

    const newOptions: { label: string; value: string }[] = [];
    const newSelectedChannels: string[] = [];

    const { sms_count = [], whatsapp_count = [] } = updateModeTravellerCount;

    if (sms_count.length > 0) {
      newOptions.push({ label: "SMS", value: "sms" });
      newSelectedChannels.push("sms");
    }

    if (whatsapp_count.length > 0) {
      newOptions.push({ label: "WhatsApp", value: "whatsapp" });
      newSelectedChannels.push("whatsapp");
    }

    setUpdateModeChannelOptions(newOptions);
    setSelectedChannels(newSelectedChannels);
  }, [updateCampaignId, updateModeTravellerCount]);

  useEffect(() => {
    if (!updateModeTravellerCount || targetCountries.length === 0 || !updateCampaignId) return;


    console.log('🎯 Target countries ready:', targetCountries.length, 'at', Date.now()); // ADD THIS
    console.log('📊 Processing traveller count mapping...'); // ADD THIS

    const smsDataMapped = updateModeTravellerCount.sms_count
      .map(({ fromcountry, count }) => {
        const country = targetCountries.find((c) => c.country_iso === fromcountry)?.country_name;
        return country ? { country, count: String(count) } : null;
      })
      .filter(Boolean) as CountryTraveller[];

    const whatsappDataMapped = updateModeTravellerCount.whatsapp_count
      .map(({ fromcountry, count }) => {
        const country = targetCountries.find((c) => c.country_iso === fromcountry)?.country_name;
        return country ? { country, count: String(count) } : null;
      })
      .filter(Boolean) as CountryTraveller[];

    setSmsData(smsDataMapped);
    setWhatsappData(whatsappDataMapped);
  }, [updateModeTravellerCount, targetCountries, updateCampaignId]);




  // Add useEffect to set draft mode channel options (similar to update mode):

  useEffect(() => {
    if (!isDraftMode || !draftModeTravellerCount) return;

    const newOptions: { label: string; value: string }[] = [];
    const newSelectedChannels: string[] = [];

    const { sms_count = [], whatsapp_count = [] } = draftModeTravellerCount;

    if (sms_count.length > 0) {
      newOptions.push({ label: "SMS", value: "sms" });
      newSelectedChannels.push("sms");
    }

    if (whatsapp_count.length > 0) {
      newOptions.push({ label: "WhatsApp", value: "whatsapp" });
      newSelectedChannels.push("whatsapp");
    }

    setDraftModeChannelOptions(newOptions);
    setSelectedChannels(newSelectedChannels);
  }, [isDraftMode, draftModeTravellerCount]);

  // Add useEffect to populate traveller count data (similar to update mode):

  useEffect(() => {
    // Add safety checks to prevent race conditions  
    if (!isDraftMode || !draftModeTravellerCount) return;

    // Wait for targetCountries to be loaded
    if (!targetCountries || targetCountries.length === 0) {
      console.log("Draft mode: Waiting for targetCountries to load...");
      return;
    }

    console.log("Processing draft mode traveller count mapping", {
      draftModeTravellerCount,
      targetCountriesCount: targetCountries.length
    });

    const smsDataMapped = draftModeTravellerCount.sms_count
      .map(({ fromcountry, count }) => {
        const country = targetCountries.find((c) => c.country_iso === fromcountry)?.country_name;
        if (!country) {
          console.warn(`Draft SMS: Country not found for ISO: ${fromcountry}`);
          return null;
        }
        return { country, count: String(count) };
      })
      .filter(Boolean) as CountryTraveller[];

    const whatsappDataMapped = draftModeTravellerCount.whatsapp_count
      .map(({ fromcountry, count }) => {
        const country = targetCountries.find((c) => c.country_iso === fromcountry)?.country_name;
        if (!country) {
          console.warn(`Draft WhatsApp: Country not found for ISO: ${fromcountry}`);
          return null;
        }
        return { country, count: String(count) };
      })
      .filter(Boolean) as CountryTraveller[];

    console.log("Draft mapped traveller data", { smsDataMapped, whatsappDataMapped });

    setSmsData(smsDataMapped);
    setWhatsappData(whatsappDataMapped);
  }, [draftModeTravellerCount, targetCountries, isDraftMode]);


  const mergeChannelOptions = (arr1: any[], arr2: any[]) => {
    const map = new Map();
    [...arr1, ...arr2].forEach(channel => {
      map.set(channel.value, channel);
    });
    return Array.from(map.values());
  };

  return (
    <>
      <Card className="w-[600px] mt-4 p-4 shadow-sm">
        <div className="text-left">
          <h3 className="text-base font-bold text-[#020617]">Channels</h3>

          <div className="mt-4">
            <Label
              htmlFor="channels"
              className="block text-sm font-medium text-[#020617] mb-1"
            >
              Select your campaign channel
            </Label>
            <MultiSelectFromCountry
              options={
                // updateCampaignId 
                //   ? updateModeChannelOptions 
                //   : isDraftMode 
                //     ? draftModeChannelOptions 
                //     : channelOptions

                isUpdateMode
                  ? updateModeChannelOptions
                  : isDraftMode
                    ? mergeChannelOptions(draftModeChannelOptions, channelOptions)
                    : channelOptions

              }
              selectedValues={selectedChannels}
              setSelectedValues={setSelectedChannels}
              onValueChange={handleChannelChange}
              placeholder="Select your campaign channel"
              className="mt-1 text-black placeholder-[#64748B] text-sm font-normal"
              disabled={isViewMode || isUpdateMode}
            />

            {contentError && isSubmitAttempted && (
              <p className="text-red-500 text-xs font-medium mt-1 font-sans italic ml-1 text-left">
                {contentError}
              </p>
            )}
          </div>

          <div className="mt-4">
            <Accordion type="multiple" className="w-full">
              {selectedChannels.includes("whatsapp") && (
                <ChannelAccordionSection
                  channel="whatsapp"
                  data={whatsappData}
                  onChange={updateWhatsappData}
                  total={calculateTotal(whatsappData)}
                  onOpenCreate={() => setIsWhatsappDialogOpen(true)}
                  contentExists={!!whatsappContent}
                  isUpdateMode={isUpdateMode} // Disable editing in both update and draft mode
                />
              )}

              {selectedChannels.includes("sms") && (
                <ChannelAccordionSection
                  channel="sms"
                  data={smsData}
                  onChange={updateSmsData}
                  total={calculateTotal(smsData)}
                  onOpenCreate={() => setIsSmsDialogOpen(true)}
                  contentExists={!!smsContent}
                  isUpdateMode={isUpdateMode} // Disable editing in both update and draft mode
                />
              )}
            </Accordion>
          </div>
        </div>
      </Card>

      <Dialog open={isWhatsappDialogOpen} onOpenChange={setIsWhatsappDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <CreateContentWhatsapp onClose={() => setIsWhatsappDialogOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={isSmsDialogOpen} onOpenChange={setIsSmsDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <CreateContentSms onClose={() => setIsSmsDialogOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CampaignChannelCard;